<template>
  <div class="w-full h-20 bg-gray-200" v-if="loading">
    <loading />
  </div>
  <div
    class="w-full relative cursor-pointer"
    :class="{ ' border': !swipeable }"
    :style="{ borderColor: localCategory.color }"
    @click="selection ? emitSelect() : showDetails()"
    v-else
  >
    <div class="absolute h-full z-0 inset-0 grid grid-cols-2" v-if="swipeable">
      <div class="p-6 flex items-center bg-gray-100" :class="{ 'bg-transparent': finished }">
        <icon :name="finished ? 'Undo' : 'Check'" size="1.4rem" />
      </div>
      <div
        class="p-6 flex items-center bg-gray-300 justify-end"
        :class="{ 'bg-transparent': finished }"
      >
        <icon name="Target" size="1.4rem" />
      </div>
    </div>
    <div
      class="relative w-full grid grid-cols-auto-1-auto z-10"
      :class="{
        'transition-spacing duration-200': transitionEnabled,
        'min-h-12': selection,
        ' min-h-20': !selection,
      }"
      v-hammer:pan.horizontal="swipeable ? updateXPos : () => {}"
      v-hammer:panend="swipeable ? exitDragging : () => {}"
      v-hammer:swipe.horizontal="swipeable ? executeAction : () => {}"
      :style="activityStyle"
      v-if="localActivity || true"
      ref="activity"
    >
      <div
        class="text-white w-16 flex items-center justify-center"
        :style="{
          backgroundColor: !swipeable
            ? lightColor
            : finished
            ? lightColor
            : localCategory.color,
          color: !swipeable ? darkColor : swipeable && finished ? localCategory.color : '#fff',
        }"
      >
        <icon
          :name="
            sending
              ? 'Loading'
              : swipeable && finished
              ? localCategory.icon
              : localCategory.icon
          "
          size="1.4rem"
        />
      </div>
      <div
        class="text-sm tracking-wider grid grid-cols-1-auto content-center"
        :class="{ 'p-4 py-2': selection, 'p-4': !selection }"
        :style="{
          backgroundColor: lightColor,
          color: swipeable ? darkColor : darkColor,
        }"
      >
        <div>
          <div class="font-medium">{{ localActivity.title }}</div>
          <div v-if="swipeable && lastDate">{{ lastDate | formatDate }}</div>
        </div>
        <div class="w-8 flex items-center" v-if="!swipeable && !selection">
          <loading v-if="favoriteSending" />
          <wd-button
            transparent
            @input="isUserActivity ? removeUserActivity() : addUserActivity()"
            v-else
          >
            <icon class="-mt-1" name="Star" size="1.4em" :settings="{ filled: isUserActivity }" />
          </wd-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateMixin from "@/mixins/DateMixin";

import { ActivityModel } from "../viewmodels/Activity";
import { ActivityCategory } from "../viewmodels/ActivityCategory";

export default {
  name: "Activity",
  mixins: [DateMixin],
  data() {
    return {
      localActivity: null,
      xPos: 0,
      yPos: 0,
      isDragging: false,
      lastX: 0,
      lastY: 0,
      transitionEnabled: true,
      sending: false,
      favoriteSending: false
    };
  },
  props: {
    activity: {
      type: ActivityModel
    },
    activityId: {
      type: String
    },
    category: {
      default: null,
      type: ActivityCategory
    },
    swipeable: {
      default: false,
      type: Boolean
    },
    selection: {
      default: false,
      type: Boolean
    }
  },
  components: {},
  methods: {
    updateXPos(ev) {
      if (!this.isDragging) {
        this.isDragging = true;
        this.transitionEnabled = false;
      }

      let maxDistance = 120;
      let distanceX =
        ev.deltaX > maxDistance
          ? maxDistance
          : ev.deltaX < -maxDistance
          ? -maxDistance
          : ev.deltaX;

      this.xPos = distanceX;

      if (Math.abs(ev.deltaY) > 15) {
        this.exitDragging();
      }
    },
    executeAction(ev) {
      if (ev.type === "swiperight") {
        this.leftAction();
      } else {
        this.rightAction();
      }
    },
    leftAction() {
      if (this.finished) {
        this.unFinishActivity();
      } else {
        this.finishActivity();
      }
    },
    rightAction() {
      this.$router.push({
        path: "/create-target",
        query: { activity: this.localActivity.id }
      });
    },
    async addUserActivity() {
      this.favoriteSending = true;
      await this.$store.getters["useractivity/api"].Create({
        activity_id: this.localActivity.id
      });
      this.favoriteSending = false;
    },
    async removeUserActivity() {
      this.favoriteSending = true;
      let userActivity = this.getUserActivityForActivity(this.localActivity.id);
      await this.$store.getters["useractivity/api"]
        .Destroy(userActivity.id)
        .then()
        .catch(e => {
          this.popup({
            type: "alert",
            title: this.$t("general.error"),
            text: this.$t("activities.no_activityentry_found")
          });
        });
      this.favoriteSending = false;
    },
    getUserActivityForActivity(Id) {
      return this.$store.getters["useractivity/userActivityForActivity"](Id);
    },
    exitDragging() {
      this.transitionEnabled = true;
      this.xPos = 0;
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    },
    async finishActivity() {
      if (this.sending) return;
      this.sending = true;
      await this.$store.getters["activityentry/api"].Custom.Check(
        this.localActivity.id
      );
      this.popup({
        type: "success",
        title: this.$t("activities.activity_finished"),
        text: this.$t("activities.activity_finished_message", {
          activity: this.activity.title
        }),
        actions: [
          {
            title: this.$t("general.undo"),
            location: "right",
            action: () => {
              this.unFinishActivity();
            }
          }
        ],
        autohide: true
      });
      this.sending = false;
    },
    async unFinishActivity() {
      if (this.sending) return;
      this.sending = true;
      await this.$store.getters["activityentry/api"].Custom.Uncheck(
        this.localActivity.id
      );
      this.popup({
        type: "info",
        title: this.$t("general.reverted_changes"),
        autohide: true
      });
      this.sending = false;
    },
    createTarget() {
      this.$router.push("/create-target/" + this.localActivity.id);
    },
    adjustColor(color, amount) {
      return (
        "#" +
        color
          .replace(/^#/, "")
          .replace(/../g, color =>
            (
              "0" +
              Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(
                16
              )
            ).substr(-2)
          )
      );
    },
    showDetails() {
      if (!this.isDragging && this.xPos === 0) {
        this.$router.push("/activity/" + this.localActivity.id);
      }
    },
    emitSelect() {
      this.$emit("select", this.localActivity);
    }
  },
  computed: {
    lastEntry() {
      return this.$store.getters["activityentry/lastEntryForActivity"](
        this.localActivity.id
      );
    },
    lastDate() {
      return this.lastEntry ? this.lastEntry.created_at : false;
    },
    finished() {
      return this.lastDate ? this.isToday(this.lastDate) : false;
    },
    lightColor() {
      return this.finished && this.swipeable
        ? "#f2f2f2"
        : this.adjustColor(this.localCategory.color, 130);
    },
    darkColor() {
      return this.finished && this.swipeable
        ? "#333333"
        : this.adjustColor(this.localCategory.color, -150);
    },
    activityStyle() {
      return { marginLeft: this.xPos + "px" };
    },
    isUserActivity() {
      return this.$store.getters["useractivity/isUserActivity"](
        this.localActivity.id
      );
    }
  },
  async created() {
    if (this.activity) {
      this.localActivity = this.activity;
    } else if (!this.activity && this.activityId) {
      this.localActivity = await this.$store.getters["activity/api"].Show(
        this.activityId
      );
    }

    if (this.category) {
      this.localCategory = this.category;
    } else {
      this.localCategory = await this.$store.getters[
        "activitycategory/api"
      ].Show(this.localActivity.activity_category_id);
    }

    if (this.swipeable) {
      await this.$store.getters["activityentry/api"].Custom.ActivityLast(
        this.localActivity.id
      );
    }
    this.loaded();
  }
};
</script>
